import { Box, Grid, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  DYNAMIC_VIEW_REDUCER_ACTIONS,
  useDynamicViewState,
  useDynamicViewDispatch,
} from '../../context/dynamic-view-context';
import { ChartSelector } from '../layouts-management/ChartSelector';
import DpCard from './DpCard';
import { v4 as uuidv4 } from 'uuid';
import { getObjetivoDefaultStep } from '../../utils/default-step';

/**
 * @fileoverview Componente para añadir nuevas tarjetas al Panel Dinámico.
 * @module NewSlotCard
 */

/**
 * Componente NewSlotCard.
 *
 * Renderiza un espacio "placeholder" al final del grid de widgets cuando el
 * panel está en modo edición. Permite al usuario añadir nuevos gráficos o
 * componentes al dashboard.
 *
 * Funcionalidades:
 * - Se muestra como un banner de ancho completo (12 columnas).
 * - Abre el selector de gráficos (`ChartSelector`) al hacer clic.
 * - Añade la nueva configuración al estado global `activeLayout`.
 *
 * @returns {React.ReactElement|null} Tarjeta de nueva ranura o null si no está en modo edición.
 * 
 * @author David Maldonado
 * (c) Servicios de Monitoreo S.A. - Santiago, Chile
 */
export function NewSlotCard() {
  const { t } = useTranslation();
  const state = useDynamicViewState();
  const dispatch = useDynamicViewDispatch();
  const [isHovered, setIsHovered] = React.useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);

  function handleSubmitNewComponent({
    selectedComponentKey,
    newComponentTitle,
  }) {
    const pasoId = getObjetivoDefaultStep({
      objetivoId: state.globalObjective.objetivoId,
    });
    dispatch({
      type: DYNAMIC_VIEW_REDUCER_ACTIONS.ADD_NEW_CARD,
      payload: {
        id: uuidv4(),
        component: selectedComponentKey,
        title: newComponentTitle,
        cols: 6, // Tamaño por defecto para nuevas cards
        height: 90,
        objetivoId: state.globalObjective.objetivoId,
        customProperties: { pasoId },
      },
    });
    setIsDrawerOpen(false);
  }

  if (!state.activeLayoutId) {
    return null;
  }

  if (!state.isEditing) {
    return null;
  }

  return (
    <Grid
      item
      xs={12}
      style={{ marginTop: '20px', marginBottom: '20px' }}
    >
      <ChartSelector
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSubmit={handleSubmitNewComponent}
      />
      <Box
        onClick={() => setIsDrawerOpen(true)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          width: '100%',
          padding: '24px',
          borderRadius: '16px',
          backgroundColor: isHovered ? '#F0E7FF' : '#F5F3FF',
          border: `2px dashed ${isHovered ? '#7C3AED' : '#C084FC'}`,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          transition: 'all 0.2s ease-in-out',
        }}
      >
        <Box
          style={{
            backgroundColor: '#E9E4FF',
            borderRadius: '50%',
            width: '48px',
            height: '48px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginRight: '20px',
            color: '#6D28D9',
          }}
        >
          <AddIcon fontSize="large" />
        </Box>
        <Box>
          <Typography
            variant="h6"
            style={{
              color: '#37248F',
              fontWeight: '700',
              fontSize: '1.1rem',
            }}
          >
            {t('newSlotCard.addNewCard')}
          </Typography>
          <Typography
            variant="body2"
            style={{
              color: '#6B7280',
              marginTop: '4px',
            }}
          >
            {t('newSlotCard.addNewCardDescription') || 'Agrega nuevas tarjetas de indicadores para que queden fijas en esta sección.'}
          </Typography>
        </Box>
      </Box>
    </Grid>
  );
}
